import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { eventSearchFormDefaultValues } from '../configuration';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { EventSearchFormType } from '../types';
import { FrPage } from '../../fr-page-layout/fr-page';
import { EventSearchResults } from './event-search-results';
import { EventSearchFormBody } from '../event-search-form-body';
import { EventSearchFormFooter } from '../event-search-form-footer';
import { EventSearchSavedSearches } from '../event-search-saved-searches';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { PageHeader } from '../../fr-page-layout/search-pages/page-header';
import { FormWrapper } from '../../fr-page-layout/search-pages/form-wrapper';
import { useEventSearchBroadcaster } from '../use-event-search-broadcaster';
import { useEventSearchBootstrap } from '../use-event-search-bootstrap';
import { prepareForSubmit } from '../utils';
import { clearWindowHistoryState } from '../../common/helpers/utils';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { useFrEventSearchStateContext } from '../fr-event-search-state-provider';

export function EventSearch() {
    const {
        mutation,
        resultsOrSavedShown,
        showSavedSearches,
        runSavedSearch,
        closeSearches,
        openSavedSearch,
        searchData,
        savedSearchId,
        uniqueSearch,
        setSearchData,
        setSavedSearchId,
        showSearchResults,
        dateRangeOverride,
        getDraftFormValues,
        setDraftFormValues,
        clearDraftFormValues,
    } = useFrEventSearchStateContext();
    const { isFirstResponder } = useApplicationType();

    const setSearchDataAndClearDraft = useCallback(
        (search: Parameters<typeof setSearchData>[0]) => {
            clearDraftFormValues();
            setSearchData(search);
        },
        [clearDraftFormValues, setSearchData]
    );

    useEventSearchBroadcaster(setSearchDataAndClearDraft, openSavedSearch);

    const { defaultFuzzyMatchingEnabled, defaultIncludeActiveEnabled } = useEnabledFeatures();

    const defaultValues = useMemo(
        () => ({
            ...eventSearchFormDefaultValues,
            fuzzyMatchingEnabled: defaultFuzzyMatchingEnabled,
            includeActiveEvents: defaultIncludeActiveEnabled,
        }),
        [defaultFuzzyMatchingEnabled, defaultIncludeActiveEnabled]
    );

    const form = useForm<EventSearchFormType>({
        mode: 'onBlur',
        defaultValues,
    });
    const { getValues, reset, watch } = form;
    const didRestoreDraftFormValuesRef = useRef(false);

    const query = useEventSearchBootstrap();

    const eventSearchBootstrapResponse = query.data;

    const fuzzyMatchingEnabled = useWatch({
        control: form.control,
        name: 'fuzzyMatchingEnabled',
    });

    const onSubmit = useCallback(
        (eventSearchForm: EventSearchFormType): void => {
            clearDraftFormValues();
            // @ts-expect-error
            mutation.mutate(prepareForSubmit(eventSearchForm));
        },
        [clearDraftFormValues, mutation]
    );

    useEffect(
        function restoreDraftFormValues() {
            if (didRestoreDraftFormValuesRef.current) {
                return;
            }

            didRestoreDraftFormValuesRef.current = true;
            const draftFormValues = getDraftFormValues();
            if (draftFormValues) {
                // Restore the saved draft without replacing the defaults used by Clear All Fields.
                reset(draftFormValues, { keepDefaultValues: true });
            }
        },
        [getDraftFormValues, reset]
    );

    useEffect(
        function preserveDraftFormValues() {
            if (resultsOrSavedShown) {
                return;
            }

            setDraftFormValues(getValues());

            // watch(callback) is RHF's subscription API, which lets us clean up on unmount.
            const subscription = watch((formValues) => {
                setDraftFormValues(formValues as EventSearchFormType);
            });

            return () => subscription.unsubscribe();
        },
        [getValues, resultsOrSavedShown, setDraftFormValues, watch]
    );

    const runSavedSearchAndClearDraft = useCallback(
        (...args: Parameters<typeof runSavedSearch>) => {
            clearDraftFormValues();
            runSavedSearch(...args);
        },
        [clearDraftFormValues, runSavedSearch]
    );

    const closeSearchesAndClearLocationState = () => {
        clearDraftFormValues();
        closeSearches();
        clearWindowHistoryState();
    };

    return (
        <FrPage>
            <PageHeader
                text="Event Search"
                resultsOrSavedShown={resultsOrSavedShown}
                openSavedSearch={openSavedSearch}
                showSavedSearches={showSavedSearches}
                closeSearches={closeSearchesAndClearLocationState}
            />

            <IntegratedFormProvider
                form={form}
                query={query}
                mutation={mutation}
                fuzzyMatchingEnabled={fuzzyMatchingEnabled}
                onSubmit={onSubmit}
            >
                <FormWrapper
                    query={query}
                    form={form}
                    resultsOrSavedShown={resultsOrSavedShown}
                    formBody={
                        <EventSearchFormBody
                            isFirstResponder={isFirstResponder}
                            form={form}
                            options={eventSearchBootstrapResponse.eventSearchOptions}
                            statics={eventSearchBootstrapResponse.eventSearchStatics}
                        />
                    }
                    footer={<EventSearchFormFooter form={form} />}
                />
                {showSavedSearches && (
                    <EventSearchSavedSearches onSavedSearchClick={runSavedSearchAndClearDraft} />
                )}
            </IntegratedFormProvider>
            {showSearchResults && searchData && (
                <EventSearchResults
                    savedSearchId={savedSearchId}
                    searchData={searchData}
                    closeSearch={closeSearches}
                    searchTab={uniqueSearch}
                    setSavedSearchId={setSavedSearchId}
                    dateRangeOverride={dateRangeOverride}
                />
            )}
        </FrPage>
    );
}
